import React, { useEffect, useRef } from 'react';
import { useAdSense } from './AdSenseProvider';
import { ADSENSE_CONFIG, AD_FORMATS, AdFormat } from './adConfig';
import './AdUnit.css';

interface AdUnitProps {
  /** The slot ID from your AdSense dashboard (leave empty until you have one) */
  slotId?: string;
  /** Ad format - determines dimensions */
  format?: AdFormat;
  /** Custom width override */
  width?: number;
  /** Custom height override */
  height?: number;
  /** Additional CSS class */
  className?: string;
  /** Whether this ad should be responsive */
  responsive?: boolean;
}

/**
 * AdUnit Component
 * 
 * Displays a Google AdSense ad unit.
 * Before verification: Shows a placeholder
 * After verification: Shows actual ads once you add slot IDs
 * 
 * Usage:
 * <AdUnit slotId="1234567890" format="skyscraper" />
 */
export const AdUnit: React.FC<AdUnitProps> = ({
  slotId,
  format = 'skyscraper',
  width,
  height,
  className = '',
  responsive = false,
}) => {
  const adRef = useRef<HTMLModElement>(null);
  const { isLoaded, isEnabled } = useAdSense();
  const hasInitialized = useRef(false);

  // Get dimensions from format or use custom
  const dimensions = AD_FORMATS[format];
  const adWidth = width ?? dimensions.width;
  const adHeight = height ?? dimensions.height;

  useEffect(() => {
    // Only initialize ad if:
    // 1. Ads are enabled
    // 2. Script is loaded
    // 3. We have a slot ID
    // 4. Haven't already initialized this ad
    if (!isEnabled || !isLoaded || !slotId || hasInitialized.current) {
      return;
    }

    try {
      const adsbygoogle = (window as any).adsbygoogle;
      if (adsbygoogle && adRef.current) {
        adsbygoogle.push({});
        hasInitialized.current = true;
      }
    } catch (error) {
      console.error('[AdUnit] Error initializing ad:', error);
    }
  }, [isLoaded, isEnabled, slotId]);

  // Don't render anything if ads are completely disabled
  if (!isEnabled) {
    return null;
  }

  // Show placeholder if no slot ID yet (verification stage)
  if (!slotId) {
    return (
      <div 
        className={`ad-unit ad-unit--placeholder ${className}`}
        style={{ width: adWidth, height: adHeight }}
      >
        <div className="ad-unit__placeholder-content">
          <span className="ad-unit__placeholder-icon">📢</span>
          <span className="ad-unit__placeholder-text">Ad Space</span>
          <span className="ad-unit__placeholder-subtext">Coming Soon</span>
        </div>
      </div>
    );
  }

  // Render actual ad unit
  return (
    <div 
      className={`ad-unit ${className}`}
      style={{ width: adWidth, height: adHeight }}
    >
      <ins
        ref={adRef}
        className="adsbygoogle"
        style={{
          display: 'block',
          width: responsive ? '100%' : adWidth,
          height: responsive ? 'auto' : adHeight,
        }}
        data-ad-client={ADSENSE_CONFIG.publisherId}
        data-ad-slot={slotId}
        data-ad-format={responsive ? 'auto' : undefined}
        data-full-width-responsive={responsive ? 'true' : undefined}
      />
    </div>
  );
};

export default AdUnit;
